import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
  Signal,
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { isDefined } from '@trimble-gcs/common';
import { TranslationService } from '@trimble-gcs/i18n';
import { ModusButtonModule, ModusIconModule, ModusTooltipModule } from '@trimble-gcs/modus';
import { filter } from 'rxjs';
import { DialogComponent } from '../../../dialog/dialog.component';
import { DialogData } from '../../../dialog/dialog.model';
import { DialogService } from '../../../dialog/dialog.service';
import { SetView } from '../../options-panel.actions';
import { OptionsPanelView } from '../../options-panel.models';
import { DownloadProgressComponent } from '../download-progress/download-progress.component';
import { ClearCompletedDownloads } from '../download.actions';
import { DownloadStatus, FileDownload, ScanDownload } from '../download.models';
import { DownloadService } from '../download.service';
import { DownloadState } from '../download.state';
import { downloadStatusTranslations } from './download-status.translations';

interface ScanDownloadView extends ScanDownload {
  files: FileDownloadView[];
}

interface FileDownloadView extends FileDownload {
  downloadProgressTooltip?: string;
}

@UntilDestroy()
@Component({
  selector: 'sd-download-status',
  imports: [
    CommonModule,
    ModusTooltipModule,
    ModusButtonModule,
    ModusIconModule,
    DownloadProgressComponent,
  ],
  templateUrl: './download-status.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DownloadStatusComponent implements OnDestroy {
  private readonly store = inject(Store);
  private readonly downloadService = inject(DownloadService);
  private readonly dialogService = inject(DialogService);
  private readonly translationService = inject(TranslationService);

  readonly translations = downloadStatusTranslations();

  private readonly scanDownloads = this.store.selectSignal(DownloadState.downloads);
  readonly scanDownloadViews = this.getScanDownloadViews();
  readonly hasDownloads = computed(() => this.scanDownloads().length > 0);

  private readonly fileDownloads = this.getFileDownloads();
  readonly overallProgress = this.getOverallProgress();
  readonly overallProgressTooltip = this.getOverallProgressTooltip();
  readonly clearDisabled = this.isClearDisabled();
  readonly cancelDisabled = this.isCancelDisabled();

  private cancelDialogRef: MatDialogRef<DialogComponent> | null = null;

  ngOnDestroy(): void {
    this.cancelDialogRef?.close();
  }

  close() {
    this.store.dispatch(new SetView(OptionsPanelView.None, false));
  }

  toggleExpanded(item: ScanDownload) {
    item.expanded = !item.expanded;
  }

  clearCompleted() {
    this.store.dispatch(new ClearCompletedDownloads());
  }

  cancelDownloads() {
    this.showCancelConfirmation().subscribe(() => this.downloadService.cancelAllDownloads());
  }

  private getScanDownloadViews(): Signal<ScanDownloadView[]> {
    return computed(() =>
      this.scanDownloads().map((scan) => ({
        ...scan,
        files: scan.files.map((file) => ({
          ...file,
          downloadProgressTooltip: isDefined(file.downloadProgress)
            ? this.translationService.translate(this.translations.fileProgress.tooltip.text, {
                percentageComplete: file.downloadProgress.percentageComplete,
              })
            : undefined,
        })),
      })),
    );
  }

  private getFileDownloads() {
    return computed(() =>
      this.scanDownloads().flatMap((scan) => {
        return scan.files.filter((file) => isDefined(file.downloadProgress));
      }),
    );
  }

  private getOverallProgress() {
    return computed(() => {
      const files = this.fileDownloads();
      return files.length > 0
        ? this.store.selectSnapshot(DownloadState.getDownloadsProgress(files))
        : undefined;
    });
  }

  private getOverallProgressTooltip() {
    return computed(() => {
      const progress = this.overallProgress();
      return isDefined(progress)
        ? this.translationService.translate(this.translations.overallProgress.tooltip.text, {
            percentageComplete: progress.percentageComplete,
          })
        : undefined;
    });
  }

  private isClearDisabled() {
    return computed(() => {
      const files = this.fileDownloads();

      const noDownloadsComplete = !files.some((file) => {
        const status = file.downloadProgress?.status;
        return status === DownloadStatus.Complete || status === DownloadStatus.Failed;
      });

      return files.length === 0 || noDownloadsComplete;
    });
  }

  private isCancelDisabled() {
    return computed(() => {
      const files = this.fileDownloads();

      const allDownloadsComplete = files.every((file) => {
        const status = file.downloadProgress?.status;
        return status === DownloadStatus.Complete || status === DownloadStatus.Failed;
      });

      return files.length === 0 || allDownloadsComplete;
    });
  }

  private showCancelConfirmation() {
    const dialogData = new DialogData(
      this.translations.confirmCancelDialog.title.text,
      this.translations.confirmCancelDialog.message.text,
      { text: this.translations.confirmCancelDialog.yes.text, color: 'danger' },
      { text: this.translations.confirmCancelDialog.no.text, color: 'secondary' },
    );

    this.cancelDialogRef = this.dialogService.showMessage(dialogData);

    return this.cancelDialogRef.afterClosed().pipe(filter((confirmed) => confirmed ?? false));
  }
}
